import { postDataAPI } from './api'

/**
 * Maps a track object returned by the discovery endpoint into the shape
 * that `user/saveCatalog` expects. Shared between MusicPage bulk import
 * and onboarding auto-pull so both paths go through the same pipeline.
 */
export function mapDiscoveredTrackToSong(track: any) {
  const primaryArtist =
    track?.artist_name || track?.artist || track?.primary_artist || track?.primaryArtist || ''
  const featuredArtists = Array.isArray(track?.featured_artists)
    ? track.featured_artists
    : typeof track?.featured_artists === 'string'
      ? track.featured_artists.split(',').map((a: string) => a.trim()).filter(Boolean)
      : []
  const durationSeconds =
    typeof track?.duration === 'number'
      ? track.duration
      : track?.duration_ms
        ? Math.round(track.duration_ms / 1000)
        : ''
  const spotifyId =
    track?.spotify_track_id ||
    track?.spotify_id ||
    track?.spotifyId ||
    track?.spotify_trackid ||
    ''
  const spotifyLink =
    track?.spotify_url ||
    track?.spotifyUrl ||
    track?.spotify_link ||
    track?.spotifyLink ||
    (spotifyId ? `https://open.spotify.com/track/${spotifyId}` : '')
  return {
    song_title: track?.song_title || track?.title || track?.name || '',
    status: track?.status || 'released',
    release_date: track?.release_date || track?.releaseDate || '',
    duration: durationSeconds,
    isrc: track?.isrc || track?.ISRC || '',
    artwork: track?.album_artwork || track?.albumArtwork || track?.artwork || '',
    album_artwork: track?.album_artwork || track?.albumArtwork || track?.artwork || '',
    spotify_link: spotifyLink,
    spotify_id: spotifyId || undefined,
    spotify_track_id: spotifyId || undefined,
    primary_artist: primaryArtist,
    artist_name: primaryArtist,
    featured_artists: featuredArtists.join(', '),
    primary_artists: primaryArtist ? [primaryArtist] : [],
    album_name: track?.album_name || track?.album || '',
    record_label: track?.record_label || track?.label || '',
    label: track?.record_label || track?.label || '',
    distributor: track?.distributor || '',
    genre: track?.genre || '',
    upc: track?.upc || '',
    bpm: track?.bpm || '',
    key_signature: track?.key_signature || '',
    copyright: track?.copyright || '',
    track_number: track?.track_number || '',
    disc_number: track?.disc_number || '',
    explicit: track?.explicit || false,
    songwriter: track?.songwriter || '',
    producer: track?.producer || '',
    soundcharts_data: track?.soundcharts_data || null,
    suggested_writers: track?.suggested_writers || [],
    split_status: track?.split_status || '',
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/**
 * Consolidate raw discovery tracks by ISRC before import.
 * Picks the entry with the richest metadata and fills missing fields from dupes.
 */
function consolidateByIsrc(tracks: any[]): any[] {
  const isrcMap = new Map<string, any[]>()
  const noIsrc: any[] = []

  for (const t of tracks) {
    const isrc = (t.isrc || t.ISRC || '').trim().toUpperCase()
    if (!isrc) { noIsrc.push(t); continue }
    const bucket = isrcMap.get(isrc)
    if (bucket) bucket.push(t)
    else isrcMap.set(isrc, [t])
  }

  const result: any[] = []
  for (const [, bucket] of isrcMap) {
    if (bucket.length === 1) { result.push(bucket[0]); continue }

    const scored = bucket.map((t) => ({
      track: t,
      score: Object.values(t).filter((v) => v != null && v !== '' && v !== 0).length,
    }))
    scored.sort((a, b) => b.score - a.score)

    const primary = { ...scored[0].track }
    for (const { track: other } of scored.slice(1)) {
      for (const [key, val] of Object.entries(other)) {
        if ((primary[key] === undefined || primary[key] === null || primary[key] === '') && val != null && val !== '') {
          primary[key] = val
        }
      }
      const scA = primary.soundcharts_data
      const scB = other.soundcharts_data
      if (scB && (!scA || (scB.streams ?? 0) > (scA.streams ?? 0))) {
        primary.soundcharts_data = scB
      }
    }
    result.push(primary)
  }

  result.push(...noIsrc)
  return result
}

export interface BulkImportOptions {
  batchSize?: number
  batchDelay?: number
  timeout?: number
  onProgress?: (current: number, total: number, batchIndex: number, totalBatches: number) => void
}

/**
 * Discovers an artist's catalog from Spotify and saves it via the same
 * batched `user/saveCatalog` pipeline that the MusicPage "Bulk Import"
 * uses. Returns the array of saved-item IDs.
 */
export async function discoverAndBulkImport(
  spotifyUrl: string,
  artistName: string,
  opts: BulkImportOptions = {},
): Promise<string[]> {
  const {
    batchSize = 10,
    batchDelay = 300,
    timeout = 300000,
  } = opts

  const response = await postDataAPI(
    'user/discoverCatalogFromArtist',
    { spotifyUrl, artistName },
    undefined,
    { timeout },
  )

  const rawTracks: any[] =
    response?.data?.data ??
    response?.data ??
    response?.response?.data ??
    []

  if (!Array.isArray(rawTracks) || rawTracks.length === 0) {
    console.warn('[catalogBulkImport] No tracks returned from discovery')
    return []
  }

  // Consolidate tracks that share the same ISRC before saving
  const consolidated = consolidateByIsrc(rawTracks)
  if (consolidated.length < rawTracks.length) {
    console.log(`[catalogBulkImport] Consolidated ${rawTracks.length} tracks → ${consolidated.length} unique by ISRC`)
  }

  const payloads = consolidated
    .map(mapDiscoveredTrackToSong)
    .filter((p) => p.song_title && p.primary_artist)

  if (payloads.length === 0) return []

  const total = payloads.length
  const totalBatches = Math.ceil(total / batchSize)
  const allNewIds: string[] = []

  for (let batchIndex = 0; batchIndex < totalBatches; batchIndex += 1) {
    const start = batchIndex * batchSize
    const batch = payloads.slice(start, start + batchSize)

    const res = await postDataAPI('user/saveCatalog', { data: batch })
    const saved = res?.data?.data ?? res?.data
    const batchIds = Array.isArray(saved)
      ? saved.map((item: any) => item?._id ?? item?.id).filter(Boolean)
      : []
    allNewIds.push(...batchIds)

    opts.onProgress?.(Math.min(start + batch.length, total), total, batchIndex + 1, totalBatches)

    if (batchIndex < totalBatches - 1) {
      await sleep(batchDelay)
    }
  }

  console.log(`[catalogBulkImport] Saved ${allNewIds.length}/${total} tracks via saveCatalog`)
  return allNewIds
}
